import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../hooks/useLanguage';

/**
 * Language Selector Component
 * Dropdown for switching between available languages
 */
const LanguageSelector = () => {
  const { t, currentLanguage, languages, changeLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setIsOpen(false);
  };

  const currentLang = languages.find(l => l.code === currentLanguage) || languages[0];

  return (
    <div className="language-selector" ref={dropdownRef}>
      <button
        type="button"
        ref={triggerRef}
        className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('navbar.language')}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-controls="language-menu"
      >
        <img src={currentLang.flag} alt={currentLang.name} className="language-flag" />
        <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} small`}></i>
      </button>
      
      {isOpen && (
        <div id="language-menu" className="dropdown-menu dropdown-menu-end show shadow-sm mt-2 p-1 language-menu" role="menu">
          {languages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              className={`dropdown-item d-flex align-items-center gap-2 ${
                currentLanguage === lang.code ? 'active' : ''
              }`}
              onClick={() => handleLanguageChange(lang.code)}
              role="menuitem"
              aria-current={currentLanguage === lang.code ? 'true' : undefined}
            >
              <img src={lang.flag} alt={lang.name} className="language-flag-dropdown" />
              <span>{lang.name}</span>
              {currentLanguage === lang.code && (
                <i className="bi bi-check-lg ms-auto text-primary"></i>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
